import dayjs from 'dayjs'
import { isUndefined } from 'lodash'
import merge from 'lodash/merge'
import { Channel, ChannelState } from 'phoenix'
import { defineStore } from 'pinia'

import { useAnnouncementNotifications } from '@shared/composables/use-announcement-notifications'
import { useSystemMessage } from '@shared/composables/use-system-message'
import { useWebNotifications } from '@shared/composables/use-web-notifications'
import { SocketChannels } from '@shared/models/enums/websocket-channels'
import {
  ConversationChannelEvents,
  UserChannelEvents,
} from '@shared/models/enums/websocket-events'
import { ChatNotificatioPayload } from '@shared/models/one-of-okind/chat-notification'
import {
  AssigneeUpdate,
  ChatNotification,
  ConversationParticipantsUpdate,
  NewConversation,
  NewMessage,
  NotifyChime,
  ReassignConversation,
  StatusUpdate,
} from '@shared/models/proto/base/chat'
import { Nullable } from '@shared/models/types/primary'
import { IsTypingEvent } from '@shared/models/types/websocket'
import { SessionProvider } from '@shared/plugins/session-provider'
import { PhoenixSocket } from '@shared/plugins/socket'
import { useGeneralStore } from '@shared/store/general'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import {
  BOT_TYPING_TIMEOUT,
  TYPING_TIMEOUT,
} from '@shared/utils/constants/time'
import { showBrowserNotification } from '@shared/utils/helpers/browser-notifications'
import {
  updateMessagesDeliveredStatus,
  updateMessagesReadStatus,
  updateMessagesRecallStatus,
} from '@shared/utils/helpers/chat/messages'
import { isBotConversation } from '@shared/utils/helpers/conversation/is-bot-conversation'
import { getGptConfig } from '@shared/utils/helpers/messages/bot-payload'
import { isPatientMessage } from '@shared/utils/helpers/messages/is-patient-message'
import { messageData } from '@shared/utils/helpers/messaging'
import { numeric } from '@shared/utils/helpers/numeric-formatter'
import { parseUserIds } from '@shared/utils/helpers/parse-user-ids'
import { playSound, SoundTypes } from '@shared/utils/helpers/sounds'
import { updateState } from '@shared/utils/store/http-request-state'

import { MessagePayloadUpdate } from '@protos/ampq/chat/message_payload_update_pb'
import { RefreshedMessage } from '@protos/ampq/chat/refreshed_message_pb'
import { Conversation } from '@protos/apps/vc/conversation_pb'
import { ChatStatus } from '@protos/chat/chat_status_pb'
import { Conversation as ChatConversation } from '@protos/chat/conversation_pb'
import {
  MessagePayload,
  MessagePayload_BotPayload,
  MessagePayload_Gpt,
} from '@protos/chat/message_payload_pb'
import { Message } from '@protos/chat/message_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { ConversationStatus } from '@protos/conversation_enums_pb'

import { ICreateMessageFile } from '@pc/models/proto/base/messages'
import { MessagesIndexRO } from '@pc/models/proto/ro/messages'
import { useCanAddConversation } from '@vc/composables/use-can-add-conversation'
import { ChatUserPresenceStatus } from '@vc/models/enums/chat-user'
import { ConversationSubTabs } from '@vc/models/enums/conversation-sub-tabs'
import { ChatTabs } from '@vc/models/enums/tabs'
import { ICreateMessageData } from '@vc/models/proto/base/message'
import { CreateMessageDTO } from '@vc/models/proto/dto/messages'
import { ConversationsIndexRO } from '@vc/models/proto/ro/conversations'
import routeNames from '@vc/router/route-names'
import { useChatWidgetsStore } from '@vc/store/chat-widgets'
import {
  ConversationsDataTypes,
  useConversationsStore,
} from '@vc/store/conversations'
import { useMessageAttachmentsStore } from '@vc/store/message-attachments'
import { useMessagesStore } from '@vc/store/messages'
import { useUnreadCountsStore } from '@vc/store/unread-counts'
import { useUserPreferencesStore } from '@vc/store/user-preferences'

const messages = {
  newMessage: 'You received a new message',
  file: 'File',
  newConversation: 'New conversation was assigned to you',
  reassignConversation: 'Conversation was assigned to you',
  waitingRoomBotFinishedTitle: 'Waiting room',
  waitingRoomBotFinishedBody: 'The bot conversation has finished',
}

interface State {
  chatUserChannelState: Nullable<ChannelState>
  chatWidgetChannelState: Nullable<ChannelState>
  conversationChannelState: Nullable<ChannelState>
  handledNotifications: Record<string, boolean>
  isSocketLoading: boolean
}

const state = (): State => ({
  chatUserChannelState: null,
  chatWidgetChannelState: null,
  conversationChannelState: null,
  handledNotifications: {},
  isSocketLoading: false,
})

export const useSocketStore = defineStore('socket', {
  state,

  actions: {
    async createSocketConnection(networkConnectCallback?: () => void) {
      const { createAnnouncementChannel } = useAnnouncementNotifications()
      const { createWebNotificationsChannel } = useWebNotifications()
      const peerProps = SessionProvider.apiEssentials().wsParams

      if (peerProps) {
        try {
          this.isSocketLoading = false
          const { wsToken, wsUrl } = peerProps
          await PhoenixSocket.connect({
            wsToken,
            wsUrl,
            networkConnectCallback,
          })

          await this.subscribeChatUser()
          await createWebNotificationsChannel()
          await createAnnouncementChannel()
        } finally {
          this.isSocketLoading = false
        }
      }
    },

    closeSocketConnection() {
      const { closeWebNotificationsChannel } = useWebNotifications()

      PhoenixSocket.disconnect()
      this.unSubscribeChatUser()
      closeWebNotificationsChannel()
    },

    onChangeChatUserChannelState(channelState: ChannelState) {
      this.chatUserChannelState = channelState
    },

    onChangeChatWidgetChannelState(channelState: ChannelState) {
      this.chatWidgetChannelState = channelState
    },

    onChangeConversationChannelState(channelState: ChannelState) {
      this.conversationChannelState = channelState
    },

    // ChatUserChannel
    isChatUserSubscribed() {
      return PhoenixSocket.isChannelSubscribed(
        SessionProvider.chatUserId(),
        SocketChannels.ChatUserChannel
      )
    },

    async subscribeChatUser() {
      const { channel } = await PhoenixSocket.findChannel(
        SessionProvider.chatUserId(),
        SocketChannels.ChatUserChannel,
        this.onChangeChatUserChannelState
      )
      this.subsribeToChatNotificationEvents(channel)
    },

    unSubscribeChatUser() {
      const userId = SessionProvider.chatUserId()
      PhoenixSocket.leaveChannel(userId, SocketChannels.ChatUserChannel)
    },

    async subscribeChatWidgetChannel(tab: ChatTabs, widgetId: number) {
      const isAllWidget = !widgetId || widgetId == 0
      const chatUserId = SessionProvider.chatUserId()
      // if all widget selected we connect to AllChatWidgetsChannel:{{tab}}:{{chat_user_id}}
      const widget = isAllWidget ? chatUserId : widgetId
      const socketChannel = isAllWidget
        ? SocketChannels.AllChatWidgetsChannel
        : SocketChannels.ChatWidgetChannel

      const { channel } = await PhoenixSocket.findChannel(
        `${tab}_tab:${widget}`,
        socketChannel,
        this.onChangeChatWidgetChannelState
      )
      this.subsribeToChatNotificationEvents(channel)
    },

    async unsubscribeAllChatWidgetChannels() {
      await Promise.allSettled([
        PhoenixSocket.leaveAllChannelTopics(
          SocketChannels.AllChatWidgetsChannel
        ),
        PhoenixSocket.leaveAllChannelTopics(SocketChannels.ChatWidgetChannel),
      ])
    },

    subsribeToChatNotificationEvents(channel: Channel) {
      channel.on(UserChannelEvents.NewConversation, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.ReassignConversation, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.AssigneeUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.StatusUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.NewMessage, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.RefreshedMessage, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.NotifyChime, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.ConversationParticipantsUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.MessagePayloadUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })
    },

    isCurrentChatWidget(chatWidgetId?: number) {
      const chatWidgetsStore = useChatWidgetsStore()

      return (
        chatWidgetsStore.widget.data?.id &&
        chatWidgetsStore.widget.data?.id === chatWidgetId
      )
    },

    async markChatUserPresence(status: ChatUserPresenceStatus) {
      try {
        const { channel } = await PhoenixSocket.findChannel(
          SessionProvider.chatUserId(),
          SocketChannels.ChatUserChannel
        )
        channel.push(UserChannelEvents.MarkChatUserPresence, { status })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('markChatUserPresence error:', error)
      }
    },

    async markChatUserCurrentTab(tab: ChatTabs) {
      try {
        const { channel } = await PhoenixSocket.findChannel(
          SessionProvider.chatUserId(),
          SocketChannels.ChatUserChannel
        )
        await channel.push(UserChannelEvents.MarkChatUserCurrentTab, { tab })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('markChatUserCurrentTab error:', error)
      }
    },

    sendChatMessage(dto: {
      data: ICreateMessageData
      files: ICreateMessageFile[]
    }): Promise<Message> {
      return new Promise((resolve, reject) => {
        PhoenixSocket.findChannel(
          dto.data?.conversationId,
          SocketChannels.ConversationChannel
        )
          .then(({ channel }) => {
            channel
              .push(
                ConversationChannelEvents.CreateMessageVc,
                new CreateMessageDTO(dto)
              )
              .receive('ok', resolve)
              .receive('error', reject)
              .receive('timeout', resolve)
          })
          .catch(reject)
      })
    },

    pushFileUploadFinished(conversationId: number, messageId: number) {
      PhoenixSocket.findChannel(
        conversationId,
        SocketChannels.ConversationChannel
      ).then(({ channel }) => {
        channel.push(ConversationChannelEvents.FileUploadFinished, {
          messageId,
        })
      })
    },

    pushFileUploadStarted(conversationId: number, messageId: number) {
      PhoenixSocket.findChannel(
        conversationId,
        SocketChannels.ConversationChannel
      ).then(({ channel }) => {
        channel.push(ConversationChannelEvents.FileUploadStarted, {
          messageId,
        })
      })
    },

    showNewMessageBrowserNotification(message: Message) {
      const conversationStore = useConversationsStore()

      if (message.messageType === MessageType.WAITING_BOT_FINISHED) {
        return this.showBrowserNotificationWrapper(
          message.conversationId,
          message.chatWidgetId,
          messages.waitingRoomBotFinishedTitle,
          messages.waitingRoomBotFinishedBody
        )
      }

      if (message.attachments?.length) {
        return this.showBrowserNotificationWrapper(
          message.conversationId,
          message.chatWidgetId,
          messages.newMessage,
          messages.file
        )
      }

      if (!message.text) {
        return this.showBrowserNotificationWrapper(
          message.conversationId,
          message.chatWidgetId,
          messages.newMessage
        )
      }

      const messageText = messageData(message.text).text

      const isPureText =
        !messageText?.includes('{{') && !messageText?.includes('{"')

      if (isPureText) {
        return this.showBrowserNotificationWrapper(
          message.conversationId,
          message.chatWidgetId,
          messages.newMessage,
          message.text
        )
      }

      const usersIds = parseUserIds(messageText)?.map(Number) ?? []

      const newAssignee =
        conversationStore.conversation.data?.participants?.find(
          // 1 - means the second user in a values array (the new assignee id)
          (item) => item.chatUserId === Number(usersIds[1])
        )

      if (newAssignee) {
        return this.showBrowserNotificationWrapper(
          message.conversationId,
          message.chatWidgetId,
          messages.newMessage,
          `Conversation assigned to ${newAssignee.firstName} ${newAssignee.lastName}`.trim()
        )
      }
    },

    unsubscribeConversation(conversationId: number) {
      PhoenixSocket.leaveChannel(
        conversationId,
        SocketChannels.ConversationChannel
      )
    },

    handleUnreadMessagesCount(
      newMessage: Message,
      conversationAssigneeId: number
    ) {
      const unreadCountsStore = useUnreadCountsStore()
      const generalStore = useGeneralStore()
      const conversationsStore = useConversationsStore()

      const isMessageForMe =
        generalStore.currentUser?.chatUserId === conversationAssigneeId

      const { isSystemMessage } = useSystemMessage(newMessage.messageType)
      const isCurrentConversation =
        conversationsStore.currentConversationId === newMessage.conversationId

      const messageId = isCurrentConversation ? newMessage.id : null
      const isSignOffAction =
        newMessage?.payload?.content.case === 'userPayload' &&
        newMessage.payload?.content.value.option.case === 'signOffAction'

      if (
        !isSignOffAction &&
        !isSystemMessage.value &&
        isPatientMessage(newMessage) &&
        isMessageForMe
      ) {
        unreadCountsStore.increaseUnreadMessagesCount(messageId)
      }
    },

    handleChatNotification(event: ChatNotification) {
      if (this.handledNotifications[event.uuid]) {
        return
      }

      if (event.uuid) {
        this.handledNotifications[event.uuid] = true
      }

      switch (event.payload.case) {
        case ChatNotificatioPayload.newMessage: {
          this.handleNewMessageNotification(event.payload.value, event.chime)
          if (event.payload.value.data) {
            this.handleUnreadMessagesCount(
              event.payload.value.data,
              event.payload.value.conversationAssigneeChatUserId
            )
          }

          break
        }
        case ChatNotificatioPayload.newConversation:
          this.handleNewConversationNotification(
            event.payload.value,
            event.chime
          )
          break
        case ChatNotificatioPayload.reassignConversation:
          this.handleReassignConversationNotification(event.payload.value)
          break
        case ChatNotificatioPayload.messagePayloadUpdate:
          this.handleMessagePayloadUpdate(event.payload.value)
          break
        case ChatNotificatioPayload.assigneeUpdate:
          this.handleAssigneeUpdateNotification(event.payload.value)
          break
        case ChatNotificatioPayload.statusUpdate:
          this.handleStatusUpdateNotification(event.payload.value)
          break
        case ChatNotificatioPayload.refreshedMessage:
          this.handleRefreshedMessage(event.payload.value)
          break
        case ChatNotificatioPayload.notifyChime:
          this.handleNotifyChimeNotification(event.payload.value, event.chime)
          break
        case ChatNotificatioPayload.conversationParticipantsUpdate:
          this.handleConversationParticipantsUpdateNotification(
            event.payload.value
          )
      }
    },

    handleMessagePayloadUpdate(payload: MessagePayloadUpdate) {
      const messagesStore = useMessagesStore()

      const config = getGptConfig(
        new Message({
          payload: payload.data?.payload,
        })
      )

      if (payload.data?.uuid) {
        messagesStore.loadingExternalLinks[payload.data.uuid] =
          !payload.isLastChunk
      }

      const messages = messagesStore.messages.data?.data ?? []

      const message = messages.find(
        (message) => message.uuid === payload.data?.uuid
      )

      if (message && !isUndefined(config)) {
        const oldConfig = getGptConfig(
          new Message({
            payload: message.payload,
          })
        )

        const updatedConfig = Object.keys(config)
          .filter((item) => config[item as keyof typeof config].length)
          .reduce((acc, key) => {
            return {
              ...acc,
              [key]: config[key as keyof typeof config],
            }
          }, {})

        message.payload = new MessagePayload({
          content: {
            case: 'botPayload',
            value: new MessagePayload_BotPayload({
              option: {
                case: 'gpt',
                value: new MessagePayload_Gpt({
                  configDataResponse: {
                    ...oldConfig,
                    ...updatedConfig,
                  },
                }),
              },
            }),
          },
        })
      }
    },

    handleRefreshedMessage(payload: RefreshedMessage) {
      const message = payload.data
      if (!message) {
        return
      }

      const messagesStore = useMessagesStore()

      const time = dayjs().toISOString()

      const messageToAdd = merge(message, {
        actualStatus: {
          deliveredTime: time,
        },
      })

      messagesStore.updateAiMessage(messageToAdd)
    },

    async handleNewMessageNotification(payload: NewMessage, chime: boolean) {
      const { data: message } = payload
      if (!message) {
        return
      }

      const conversationStore = useConversationsStore()
      const messagesStore = useMessagesStore()
      const generalStore = useGeneralStore()

      if (payload.data?.uuid) {
        messagesStore.loadingExternalLinks[payload.data.uuid] =
          payload.shouldWaitPayloadUpdate
      }

      function updateConversation(
        conversationName: keyof ConversationsDataTypes,
        message: Message
      ) {
        const conversations = conversationStore[conversationName]
        const { data: conversationsData, meta: conversationsMeta } =
          conversations.data ?? {}

        // Update all the conversations
        if (conversationsData) {
          conversationStore[conversationName] = updateState(
            conversations,
            new ConversationsIndexRO({
              data: [
                ...conversationsData.map((obj) => {
                  if (obj.id === message.conversationId) {
                    if (message.id > (obj.lastMessage?.id ?? 0)) {
                      return { ...obj, lastMessage: message }
                    }

                    return { ...obj }
                  }

                  return obj
                }),
              ],
              meta: conversationsMeta ?? undefined,
            })
          )
          const conversation = conversationStore.conversation

          if (
            conversation.data?.id === message.conversationId &&
            message.id > (conversation.data?.lastMessage?.id ?? 0)
          ) {
            conversationStore.conversation = updateState(conversation, {
              ...conversation.data,
              lastMessage: message,
            })
          }
        }
      }

      updateConversation(ConversationSubTabs.Opened, message)
      updateConversation(ConversationSubTabs.Waiting, message)

      const isMessageFromMe = generalStore.isMe(String(message.sender?.id))
      if (!isMessageFromMe) {
        this.markDliveredMessage(message.conversationId, message.id)
      }

      if (
        [
          MessageType.ASSIGNEE_UPDATE_NOTIFICATION,
          MessageType.STATUS_UPDATE_NOTIFICATION,
        ].includes(message.messageType)
      ) {
        if (!messagesStore.messageList.length) {
          await messagesStore.fetchMessages({
            conversationId: message.conversationId,
            page: 1,
            perPage: 10,
            chatWidgetId: message.chatWidgetId,
          })
        }

        const conversation = await this.handleConversationUpdate(
          message.conversationId
        )

        if (conversation) {
          const isCurrentUserAssignee = generalStore.isMe(
            conversation?.assignee?.id
          )

          if (
            chime &&
            conversation.status === ConversationStatus.OPEN &&
            isCurrentUserAssignee
          ) {
            this.playSoundWrapper(SoundTypes.General)

            this.showBrowserNotificationWrapper(
              conversation?.id,
              conversation?.chatWidget?.id || 0,
              messages.reassignConversation
            )
          }
        }

        if (this.isCurrentChatWidget(message.chatWidgetId)) {
          conversationStore.updateWaitingRoomAttention()
        }
      }

      if (!isMessageFromMe) {
        if (!message.actualStatus) {
          message.actualStatus = new ChatStatus()
        }
        const time = new Date().toISOString()
        message.actualStatus.deliveredTime = time
      }

      messagesStore.addMessageToCurrentConversation(message)
      messagesStore.handleSpecialMessages(message)

      this.handleNewMessageChime(message, chime)

      if (
        message.messageType === MessageType.WAITING_BOT_FINISHED &&
        this.isCurrentChatWidget(message.chatWidgetId)
      ) {
        conversationStore.updateWaitingRoomAttention(message)
      }
    },

    async markDliveredMessage(conversationId: number, messageId: number) {
      const time = new Date().toISOString()

      await PhoenixSocket.findChannel(
        conversationId,
        SocketChannels.ConversationChannel
      ).then(({ channel }) => {
        channel.push(ConversationChannelEvents.MarkDelivered, {
          message_id: messageId,
          time,
        })
      })
    },

    async handleNewConversationNotification(
      payload: NewConversation,
      chime: boolean
    ) {
      const conversationId = payload?.data?.id
      if (!conversationId) {
        return
      }

      const conversationsStore = useConversationsStore()
      const conversation = await conversationsStore.fetchConversation({
        conversationId,
      })
      if (!conversation) {
        return
      }

      const generalStore = useGeneralStore()

      const { shouldDisplayConversation } = useCanAddConversation(conversation)

      if (
        this.shouldProcessEvent(conversation?.chatWidget?.id) &&
        shouldDisplayConversation(conversation.status)
      ) {
        conversationsStore.addConversation(
          conversation,
          conversationsStore.getSubTabByStatus(conversation)
        )
      }

      if (
        chime &&
        conversation.status === ConversationStatus.OPEN &&
        generalStore.isMe(conversation?.assignee?.id)
      ) {
        this.playSoundWrapper(SoundTypes.General)

        this.showBrowserNotificationWrapper(
          conversation?.id,
          conversation?.chatWidget?.id || 0,
          messages.newConversation
        )
      }
    },

    handleNewAssignmentsCount(payload: ReassignConversation) {
      const unreadCountsStore = useUnreadCountsStore()
      const generalStore = useGeneralStore()

      const conversationsStore = useConversationsStore()
      const isCurrentConversation =
        conversationsStore.currentConversationId === payload.data?.id

      if (
        this.isCurrentChatWidget(payload.data?.chatWidgetId) &&
        isCurrentConversation
      ) {
        return
      }
      if (
        payload.data?.assignee?.id === numeric(generalStore.currentUser?.id)
      ) {
        unreadCountsStore.increaseAssignmentsCount()
      }
    },

    async handleReassignConversationNotification(
      payload: ReassignConversation
    ) {
      const unreadCountsStore = useUnreadCountsStore()
      await unreadCountsStore.fetchNewAssignmentsCount()

      const conversationId = payload?.data?.id

      if (!conversationId) {
        return
      }

      await this.handleConversationUpdate(conversationId)
      unreadCountsStore.fetchUnreadMessagesCount()
    },

    handleAssigneeUpdateNotification(payload: AssigneeUpdate) {
      if (this.shouldProcessEvent(payload.chatWidgetId)) {
        this.handleConversationUpdate(payload.conversationId)
      }
    },

    handleStatusUpdateNotification(payload: StatusUpdate) {
      if (this.shouldProcessEvent(payload.chatWidgetId)) {
        this.handleConversationUpdate(payload.conversationId)
      }
      if (payload.status === ConversationStatus.CLOSE) {
        // if conversation is closed we should refetch unread messages/Assignments count
        const unreadCountsStore = useUnreadCountsStore()
        unreadCountsStore.fetchUnreadMessagesCount()
        unreadCountsStore.fetchNewAssignmentsCount()
      }
    },

    handleNotifyChimeNotification(payload: NotifyChime, chime: boolean) {
      // notify all // Do we need it?
      if (chime && payload.data) {
        this.playSoundWrapper(
          payload.data?.status === ConversationStatus.ASSIGNED_TO_BOT
            ? SoundTypes.Ping
            : SoundTypes.General
        )
      }
    },

    handleConversationParticipantsUpdateNotification(
      payload: ConversationParticipantsUpdate
    ) {
      const conversation = payload.data

      if (!conversation) {
        return
      }

      this.handleConversationParticipantsUpdate(conversation)
    },

    shouldProcessEvent(chatWidgetId?: Nullable<number>) {
      if (!chatWidgetId) {
        return true
      }

      const chatWidgetsStore = useChatWidgetsStore()

      const isAllConversationsSelected = !chatWidgetsStore.widget.data
      return (
        this.isCurrentChatWidget(chatWidgetId) || isAllConversationsSelected
      )
    },

    async handleConversationUpdate(conversationId: Conversation['id']) {
      const conversationsStore = useConversationsStore()

      // fetch conversation from the API
      const conversation = await conversationsStore.fetchConversation({
        conversationId,
      })

      if (!conversation) {
        return
      }

      if (!this.shouldProcessEvent(conversation?.chatWidget?.id || 0)) {
        return conversation
      }

      const { shouldDisplayConversation } = useCanAddConversation(conversation)
      const shouldDisplay = shouldDisplayConversation(conversation.status)
      const prevStatus = conversationsStore.getConversation(
        conversation.id
      )?.status

      // remove conversatoin from its current list if the status is changed or it's no longer allowed to display
      if (
        !shouldDisplay ||
        (prevStatus && prevStatus !== conversation.status)
      ) {
        conversationsStore.removeConversation(conversation.id)
      }

      // add conversation to the corresponding list if it's allowed to display
      if (shouldDisplay) {
        const tabsToMoveTo = {
          [ConversationStatus.OPEN]: ConversationSubTabs.Opened,
          [ConversationStatus.ASSIGNED_TO_BOT]: ConversationSubTabs.Waiting,
          [ConversationStatus.INVITED]: ConversationSubTabs.Invited,
          [ConversationStatus.CLOSE]: ConversationSubTabs.Opened,
        } as Record<Partial<ConversationStatus>, keyof ConversationsDataTypes>

        conversationsStore.addConversation(
          conversation,
          tabsToMoveTo[conversation.status]
        )
      }

      // If it's current active conversation replace it with fresh data
      conversationsStore.updateCurrentConversation(conversation)

      // If status is changed to CLOSE and it's current active conversation then change the tab and load conversations
      if (
        conversationsStore.currentConversationId === conversation.id &&
        conversation.status === ConversationStatus.CLOSE &&
        prevStatus !== ConversationStatus.CLOSE
      ) {
        conversationsStore.setSelectedTab(ChatTabs.Closed)
        conversationsStore.fetchOpenedConversations({
          page: 1,
          chatWidgetId: conversation?.chatWidget?.id || 0,
        })
      }

      return conversation
    },

    handleConversationParticipantsUpdate({
      id: conversationId,
      chatWidgetId: chatWidgetId,
    }: ChatConversation) {
      if (!this.isCurrentChatWidget(chatWidgetId)) {
        return
      }

      const conversationsStore = useConversationsStore()
      conversationsStore.fetchAndReplaceConversationById({ conversationId })
    },

    //createConversationChannel with unsubscribing previous Conversations
    async subscribeConversation(conversationId: number) {
      const typingIndicatorsStore = useTypingIndicatorsStore()
      PhoenixSocket.leavePreviousChannels(
        conversationId,
        SocketChannels.ConversationChannel
      )
      typingIndicatorsStore.resetTypingStatus(conversationId)

      await PhoenixSocket.findChannel(
        conversationId,
        SocketChannels.ConversationChannel,
        this.onChangeConversationChannelState
      ).then(({ channel }) => {
        /**
         * @description
         *
         * issue in mobile version, when mark read a couple message after redirect, 'channel.on(ConversationChannelEvents.MarkRead' can call
         * more times that needed
         *
         * to avoid this we removes this listener from the channel first
         * */
        // TODO investigate why ConversationChannelEvents.MarkRead is called multiple times on mobile
        channel.off(ConversationChannelEvents.MarkRead)

        channel.on(ConversationChannelEvents.IsTyping, this.handleTyping)
        channel.on(
          ConversationChannelEvents.FileUploadFinished,
          this.handleFileUploadFinished
        )

        channel.on(
          ConversationChannelEvents.FileUploadStarted,
          this.handleFileUploadStarted
        )

        channel.on(
          ConversationChannelEvents.MarkDelivered,
          this.onDeliveredStatusChange
        )
        channel.on(ConversationChannelEvents.MarkRead, this.onReadStatusChange)
        channel.on(
          ConversationChannelEvents.MarkRecalled,
          this.onRecallStatusChange
        )
        channel.on(
          ConversationChannelEvents.VideoChatCancel,
          this.onVideoChatCancel
        )
      })
    },

    // TODO: add protos when available
    // eslint-disable-next-line
    onReadStatusChange(data: any) {
      const messagesStore = useMessagesStore()
      const generalStore = useGeneralStore()
      const unreadCountsStore = useUnreadCountsStore()

      const { meta, data: list = [] } = messagesStore.messages.data ?? {}

      messagesStore.messages = updateState(
        messagesStore.messages,
        new MessagesIndexRO({
          meta,
          data: updateMessagesReadStatus(data, list),
        })
      )

      const conversationsStore = useConversationsStore()
      conversationsStore.markConversationLastMessageRead(
        data?.message_id,
        data?.read_time
      )

      const { isSystemMessage } = useSystemMessage(data.messageType)

      const assignee = conversationsStore.conversation.data?.assignee

      const isOpenChatAndMessageForMe =
        conversationsStore.conversation.data?.status ===
          ConversationStatus.OPEN &&
        assignee?.chatUserId === generalStore.currentUser?.chatUserId

      if (!isSystemMessage.value && isOpenChatAndMessageForMe) {
        unreadCountsStore.decreaseUnreadMessagesCount(data.message_id)
      }
    },

    // TODO: add protos when available
    // eslint-disable-next-line
    onDeliveredStatusChange(data: any) {
      const messagesStore = useMessagesStore()
      const { meta, data: list = [] } = messagesStore.messages.data ?? {}

      messagesStore.messages = updateState(
        messagesStore.messages,
        new MessagesIndexRO({
          meta,
          data: updateMessagesDeliveredStatus(data, list),
        })
      )
    },

    // TODO: add protos when available
    // eslint-disable-next-line
    onRecallStatusChange(data: any) {
      const messagesStore = useMessagesStore()
      const { meta, data: list = [] } = messagesStore.messages.data ?? {}

      messagesStore.messages = updateState(
        messagesStore.messages,
        new MessagesIndexRO({
          meta,
          data: updateMessagesRecallStatus(data, list),
        })
      )
    },

    // TODO: add protos when available
    handleTyping({ data }: { data: IsTypingEvent }) {
      const typingIndicatorsStore = useTypingIndicatorsStore()
      if (data.user === SessionProvider.chatUserId() || !data.conversation) {
        return
      }

      const conversationsStore = useConversationsStore()
      const conversation = conversationsStore.getConversation(data.conversation)
      const typingTimeout =
        conversation &&
        isBotConversation(conversation as Conversation, data.user)
          ? BOT_TYPING_TIMEOUT
          : TYPING_TIMEOUT

      typingIndicatorsStore.setTypingStatus({
        conversationId: data.conversation,
        isTyping: data.typing,
        timeout: typingTimeout,
      })
    },

    handleFileUploadFinished({ messageId }: { messageId: number }) {
      const messageAttachmentsStore = useMessageAttachmentsStore()
      messageAttachmentsStore.fetchAttachment(messageId)
    },

    handleFileUploadStarted({ messageId }: { messageId: number }) {
      const messageAttachmentsStore = useMessageAttachmentsStore()
      messageAttachmentsStore.setPendingAttachment(messageId)
    },

    onVideoChatCancel(data: { conversation_id: number; chat_user_id: number }) {
      const videoCallConfigsStore = useVideoCallConfigsStore()
      return videoCallConfigsStore.onVideoChatCancel(
        data.conversation_id,
        data.chat_user_id
      )
    },

    async handleNewMessageChime(message: Message, chime: boolean) {
      if (
        !chime ||
        ![MessageType.TEXT, MessageType.WAITING_BOT_FINISHED].includes(
          message.messageType
        )
      ) {
        return
      }

      const generalStore = useGeneralStore()
      const conversationsStore = useConversationsStore()

      // skip when current user is sender of the message
      if (generalStore.isMe(message.sender?.id)) {
        return
      }

      // if it's current conversation then get it from store otherwise fetch via API
      const conversation =
        conversationsStore.conversation?.data?.id === message.conversationId
          ? conversationsStore.conversation?.data
          : await conversationsStore.fetchConversation({
              conversationId: message.conversationId,
            })

      // TEXT message runs chime and notification only when current user is assignee
      if (
        message.messageType === MessageType.TEXT &&
        (conversation?.status !== ConversationStatus.OPEN ||
          !generalStore.isMe(conversation?.assignee?.id))
      ) {
        return
      }

      this.playSoundWrapper(SoundTypes.General)
      this.showNewMessageBrowserNotification(message)
    },

    playSoundWrapper(type: SoundTypes) {
      const userPreferencesStore = useUserPreferencesStore()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const isVideoCallInProgress = videoCallConfigsStore.roomId

      // Don't play sound if notificaitons is muted or the user is on a video call
      if (!userPreferencesStore.isNotificationMuted && !isVideoCallInProgress) {
        playSound(type)
      }
    },

    showBrowserNotificationWrapper(
      conversationId: number,
      widgetId: number,
      title: string,
      body?: string
    ) {
      const generalStore = useGeneralStore()
      const conversationsStore = useConversationsStore()

      // Show browser notification only if user on another conversation
      // or the app is in background/minimized (document.visibilityState is 'hidden')
      if (
        !generalStore.isAppVisible ||
        conversationsStore.currentConversationId !== conversationId
      ) {
        const navigate = () => {
          // eslint-disable-next-line
          ;(this as any).router.push({
            name: routeNames.conversation,
            params: { conversationId, widgetId },
          })
        }
        showBrowserNotification(title, body, {
          silent: true,
          navigate,
        })
      }
    },

    markAssigneeAcknowledged(dto: {
      chatUserId: number
      conversationId: number
    }): Promise<{
      chatUserId: number
      conversationId: number
    }> {
      return new Promise((resolve, reject) => {
        PhoenixSocket.findChannel(
          dto.conversationId,
          SocketChannels.ConversationChannel
        )
          .then(({ channel }) => {
            channel
              .push(ConversationChannelEvents.MarkAssigneeAcknowledged, dto)
              .receive('ok', resolve)
              .receive('error', reject)
              .receive('timeout', resolve)
          })
          .catch(reject)
      })
    },
  },

  getters: {
    isChannelsConnected: (state) => {
      return state.chatUserChannelState === 'joined'
    },
  },
})
